import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { z } from "zod";

export const GITHUB_TOOLS_METADATA = [
  {
    name: "create_issue",
    description:
      "Create or file a brand new issue (a bug report or feature request) on" +
      " a specified GitHub repository, optionally with assignees and labels.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      title: z.string().describe("The title of the issue."),
      body: z.string().describe("The contents of the issue (GitHub markdown)."),
      assignees: z
        .array(z.string())
        .optional()
        .describe("Logins for Users to assign to this issue."),
      labels: z
        .array(z.string())
        .optional()
        .describe("Labels to associate with this issue."),
    },
    stake: "low",
    displayLabels: {
      running: "Creating GitHub issue",
      done: "Create GitHub issue",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "update_issue",
    description:
      "Update an existing issue on a GitHub repository: its title, body," +
      " labels, assignees, milestone, and open/closed state (closing or" +
      " reopening the issue). Only the provided fields are changed.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      issueNumber: z.number().describe("The number that identifies the issue."),
      title: z.string().optional().describe("The new title of the issue."),
      body: z
        .string()
        .optional()
        .describe("The new contents of the issue (GitHub markdown)."),
      state: z
        .enum(["open", "closed"])
        .optional()
        .describe(
          "The open or closed state of the issue. Set to 'closed' to close the" +
            " issue, or 'open' to reopen it."
        ),
      stateReason: z
        .enum(["completed", "not_planned", "reopened"])
        .optional()
        .describe(
          "The reason for the state change. Ignored unless state is changed."
        ),
      assignees: z
        .array(z.string())
        .optional()
        .describe(
          "Logins for Users to assign to this issue. Replaces the current set" +
            " of assignees; pass an empty array to clear all assignees."
        ),
      labels: z
        .array(z.string())
        .optional()
        .describe(
          "Labels to associate with this issue. Replaces the current set of" +
            " labels; pass an empty array to clear all labels."
        ),
      milestone: z
        .number()
        .nullable()
        .optional()
        .describe(
          "The number of the milestone to associate this issue with, or null" +
            " to remove the current milestone."
        ),
    },
    stake: "low",
    displayLabels: {
      running: "Updating GitHub issue",
      done: "Update GitHub issue",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_pull_request",
    description:
      "Retrieve a pull request from a specified GitHub repository including" +
      " its associated description, creation time (createdAt), merge time" +
      " (mergedAt when merged), diff, comments and reviews.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      pullNumber: z.number().describe("The pull request number."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving GitHub pull request",
      done: "Retrieve GitHub pull request",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "create_pull_request_review",
    description:
      "Approve, request changes on, or submit a review verdict for a pull request, with optional inline line comments.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      pullNumber: z
        .number()
        .describe("The number that identifies the pull request."),
      body: z.string().describe("The body text of the review."),
      event: z
        .enum(["APPROVE", "REQUEST_CHANGES", "COMMENT"])
        .describe(
          "The review action you want to perform. The review actions include: APPROVE, REQUEST_CHANGES, or COMMENT."
        ),
      comments: z
        .array(
          z.object({
            path: z
              .string()
              .describe(
                "The relative path to the file that necessitates a review comment."
              ),
            position: z
              .number()
              .optional()
              .describe(
                "The position in the diff to add a review comment as prepended in " +
                  "the diff retrieved by `get_pull_request`"
              ),
            body: z.string().describe("The text of the review comment."),
          })
        )
        .describe("File comments to leave as part of the review.")
        .optional(),
    },
    stake: "high",
    displayLabels: {
      running: "Reviewing GitHub pull request",
      done: "Review GitHub pull request",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "list_organization_projects",
    description:
      "List the open projects of a GitHub organization along with their single select fields (generally used as columns)",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Listing GitHub organization projects",
      done: "List GitHub organization projects",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "add_issue_to_project",
    description:
      "Add an existing issue to a GitHub project, optionally setting a field value.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      issueNumber: z
        .number()
        .describe("The issue number to add to the project."),
      projectId: z
        .string()
        .describe("The node ID of the GitHub project (GraphQL ID)."),
      field: z
        .object({
          fieldId: z
            .string()
            .describe("The node ID of the field to update (GraphQL ID)."),
          optionId: z
            .string()
            .describe(
              "The node ID of the option to update the field to (GraphQL ID)."
            ),
        })
        .optional()
        .describe(
          "Optional field configuration with both fieldId and optionId required if provided."
        ),
    },
    stake: "low",
    displayLabels: {
      running: "Adding GitHub issue to project",
      done: "Add GitHub issue to project",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "comment_on_issue",
    description: "Add a comment to an existing GitHub issue.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      issueNumber: z.number().describe("The issue number."),
      body: z
        .string()
        .describe("The contents of the comment (GitHub markdown)."),
    },
    stake: "low",
    displayLabels: {
      running: "Commenting on GitHub issue",
      done: "Comment on GitHub issue",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "list_discussion_categories",
    description:
      "List the available discussion categories in a GitHub repository. Use this to get the category ID required to create one.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      perPage: z
        .number()
        .min(1)
        .max(25)
        .optional()
        .describe("Results per page. Defaults to 25, max 25."),
      after: z.string().optional().describe("The cursor to start after."),
      before: z.string().optional().describe("The cursor to start before."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Listing GitHub discussion categories",
      done: "List GitHub discussion categories",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "create_discussion",
    description:
      "Create and start a brand new single GitHub discussion thread under a chosen category.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      categoryId: z
        .string()
        .describe(
          "The node ID of the GitHub discussion category. Use list_discussion_categories to get it."
        ),
      title: z.string().describe("The title of the discussion."),
      body: z
        .string()
        .describe("The contents of the discussion (GitHub markdown)."),
    },
    stake: "low",
    displayLabels: {
      running: "Creating GitHub discussion",
      done: "Create GitHub discussion",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "comment_on_discussion",
    description:
      "Add a comment to an existing GitHub discussion. Optionally reply to an existing discussion comment.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      discussionNumber: z.number().describe("The discussion number."),
      body: z
        .string()
        .describe("The contents of the comment (GitHub markdown)."),
      replyToId: z
        .string()
        .optional()
        .describe(
          "Optional node ID of the discussion comment to reply to. Omit to create a top-level comment."
        ),
    },
    stake: "low",
    displayLabels: {
      running: "Commenting on GitHub discussion",
      done: "Comment on GitHub discussion",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_discussion",
    description:
      "Retrieve a discussion from a specified GitHub repository including its description, category, and comment count.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      discussionNumber: z.number().describe("The discussion number."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving GitHub discussion",
      done: "Retrieve GitHub discussion",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_discussion_comments",
    description:
      "Retrieve, fetch, and list the comments posted on a specified GitHub discussion, with pagination.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      discussionNumber: z.number().describe("The discussion number."),
      perPage: z
        .number()
        .min(1)
        .max(100)
        .optional()
        .describe("Results per page. Defaults to 50, max 100."),
      after: z.string().optional().describe("The cursor to start after."),
      before: z.string().optional().describe("The cursor to start before."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving GitHub discussion comments",
      done: "Retrieve GitHub discussion comments",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "list_discussions",
    description:
      "List discussions in a GitHub repository: browse and enumerate the repository's discussions, returning many discussions with optional filtering.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      categoryId: z
        .string()
        .optional()
        .describe("Filter discussions by category node ID."),
      answered: z
        .boolean()
        .optional()
        .describe("Filter discussions by whether they are answered."),
      sort: z
        .enum(["CREATED_AT", "UPDATED_AT"])
        .optional()
        .describe("Sort field. Defaults to UPDATED_AT."),
      direction: z
        .enum(["ASC", "DESC"])
        .optional()
        .describe("Sort direction. Defaults to DESC."),
      perPage: z
        .number()
        .min(1)
        .max(100)
        .optional()
        .describe("Results per page. Defaults to 50, max 100."),
      after: z.string().optional().describe("Pagination cursor."),
      before: z.string().optional().describe("Pagination cursor."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Listing GitHub discussions",
      done: "List GitHub discussions",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_issue",
    description:
      "Retrieve and read the full details of a single issue from a specified GitHub repository, including its description, comments, and labels.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      issueNumber: z.number().describe("The issue number."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving GitHub issue",
      done: "Retrieve GitHub issue",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_issue_custom_fields",
    description:
      "Get custom fields set on an issue in GitHub project(s). If projectId is provided, returns custom fields for that specific project. If projectId is omitted, returns custom fields for all projects containing the issue.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      issueNumber: z.number().describe("The issue number."),
      projectId: z
        .string()
        .optional()
        .describe(
          "Optional: The node ID of a specific GitHub project (GraphQL ID). If omitted, returns custom fields for all projects containing the issue."
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving GitHub issue custom fields",
      done: "Retrieve GitHub issue custom fields",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "list_issues",
    description:
      "List issues from a specified GitHub repository with optional filtering.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      state: z
        .enum(["OPEN", "CLOSED", "ALL"])
        .optional()
        .describe("Filter issues by state. Defaults to OPEN."),
      labels: z
        .array(z.string())
        .optional()
        .describe("Filter issues by labels."),
      sort: z
        .enum(["CREATED_AT", "UPDATED_AT", "COMMENTS"])
        .optional()
        .describe("What to sort results by. Defaults to CREATED_AT."),
      direction: z
        .enum(["ASC", "DESC"])
        .optional()
        .describe("The direction of the sort. Defaults to DESC."),
      perPage: z
        .number()
        .min(1)
        .max(100)
        .optional()
        .describe("Results per page. Defaults to 50, max 100."),
      after: z.string().optional().describe("The cursor to start after."),
      before: z.string().optional().describe("The cursor to start before."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Listing GitHub issues",
      done: "List GitHub issues",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "search_advanced",
    description:
      "Search GitHub issues and pull requests using GitHub's advanced search syntax with AND/OR operators and nested filters. " +
      "Use 'is:issue' for issues, 'is:pr' for pull requests, or omit to search both. " +
      "The `@me` qualifier resolves to the authenticated account on a personal connection, but on a workspace (shared) connection it resolves to the Dust GitHub App bot, so pass the GitHub username explicitly there.",
    schema: {
      query: z
        .string()
        .describe(
          "The advanced search query string. Supports AND/OR operators and nested filters. " +
            "Examples: 'is:issue AND assignee:username AND (label:bug OR comments:>5)', 'is:pr AND assignee:@me'. " +
            "Spaces between repo/org/user filters are treated as AND operators."
        ),
      first: z
        .number()
        .min(1)
        .max(100)
        .optional()
        .describe("Number of results to return. Defaults to 30, max 100."),
      after: z.string().optional().describe("The cursor to start after."),
      before: z.string().optional().describe("The cursor to start before."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Searching GitHub issues and pull requests",
      done: "Search GitHub issues and pull requests",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "list_pull_requests",
    description:
      "List pull requests from a specified GitHub repository with optional filtering.",
    schema: {
      owner: z
        .string()
        .describe(
          "The owner of the repository (account or organization name)."
        ),
      repo: z.string().describe("The name of the repository."),
      state: z
        .enum(["OPEN", "CLOSED", "MERGED", "ALL"])
        .optional()
        .describe("Filter pull requests by state. Defaults to OPEN."),
      sort: z
        .enum(["CREATED_AT", "UPDATED_AT"])
        .optional()
        .describe("What to sort results by. Defaults to CREATED_AT."),
      direction: z
        .enum(["ASC", "DESC"])
        .optional()
        .describe("The direction of the sort. Defaults to DESC."),
      perPage: z
        .number()
        .min(1)
        .max(65)
        .optional()
        .describe("Results per page. Defaults to 30, max 65."),
      after: z.string().optional().describe("The cursor to start after."),
      before: z.string().optional().describe("The cursor to start before."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Listing GitHub pull requests",
      done: "List GitHub pull requests",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
] as const;

export const GITHUB_SERVER = {
  serverInfo: {
    name: "github",
    version: "1.0.0",
    description: "Manage issues, pull requests, and discussions.",
    authorization: {
      provider: "github",
      supported_use_cases: ["platform_actions", "personal_actions"],
    },
    icon: "GithubLogo",
    documentationUrl: null,
  },
  tools: GITHUB_TOOLS_METADATA,
} as const satisfies ServerMetadata;
